import { useRef, useState, type ReactNode } from 'react';
import { useLanguage } from '../../shell/language-context';
import { reelEmbed } from './reel-embed';
import { IconPlay } from './reel-icons';
import { compactCount } from './reel-counts';
import { ReelViewer, type ReelCard } from './reel-viewer';
import './reels.css';

// A sideways-scrolling rail of 9:16 reel tiles (design A2 / C1): YouTube's own still as
// the poster, the business (or the creator credit), a two-line caption and an "Offer"
// chip when the reel promotes one. A tile opens the fullscreen viewer at that reel; no
// player loads until then, so the rail costs the page one image per tile.
// `openId` opens the viewer on that reel at mount — the /r/{id} share link lands here (FR-C29 v2.10).
export function ReelRail({ reels, size = 'md', showBusiness = true, openId }: { reels: ReelCard[]; size?: 'sm' | 'md'; showBusiness?: boolean; openId?: string | null }) {
  const [open, setOpen] = useState<number | null>(() => {
    const i = openId ? reels.findIndex((r) => r.id === openId) : -1;
    return i >= 0 ? i : null;
  });
  const lastTile = useRef<HTMLElement | null>(null);
  if (!reels.length) return null;
  return (
    <>
      <div className={`rr rr--${size}`}>
        {reels.map((r, i) => (
          <ReelTile
            key={r.id}
            reel={r}
            showBusiness={showBusiness}
            onOpen={(el) => {
              lastTile.current = el;
              setOpen(i);
            }}
          />
        ))}
      </div>
      {open !== null ? <ReelViewer reels={reels} start={open} onClose={() => setOpen(null)} returnFocus={lastTile.current} /> : null}
    </>
  );
}

// `copy`: a repeated tile in a looping rail (the homepage marquee) — still clickable, but
// hidden from screen readers and the Tab order so each reel is announced once.
export function ReelTile({
  reel,
  showBusiness,
  onOpen,
  onFocus,
  copy = false,
  children,
}: {
  reel: ReelCard;
  showBusiness: boolean;
  onOpen: (el: HTMLElement) => void;
  onFocus?: (el: HTMLElement) => void;
  copy?: boolean;
  children?: ReactNode;
}) {
  const { t, lang } = useLanguage();
  const [noPoster, setNoPoster] = useState(false);
  const views = reel.counts.views > 0 ? t(reel.counts.views === 1 ? 'reels.views_one' : 'reels.views', { n: compactCount(lang, reel.counts.views, t) }) : '';
  const embed = reelEmbed(reel.video_id);
  const who = reel.creator ?? (showBusiness ? reel.business.name : '');
  return (
    <button
      type="button"
      className="rr__tile"
      onClick={(e) => onOpen(e.currentTarget)}
      onFocus={onFocus ? (e) => onFocus(e.currentTarget) : undefined}
      aria-label={t('reels.open', { name: reel.caption ?? reel.business.name })}
      {...(copy ? { tabIndex: -1, 'aria-hidden': true } : {})}
    >
      {embed && !noPoster ? <img className="rr__poster" src={embed.poster} alt="" loading="lazy" decoding="async" onError={() => setNoPoster(true)} /> : null}
      <span className="rr__shade" aria-hidden="true" />
      <span className="rr__src" aria-hidden="true">
        <IconPlay size={9} /> YouTube
      </span>
      {reel.offer_id ? <span className="rr__offer">{t('reels.offer_chip')}</span> : null}
      <span className="rr__play" aria-hidden="true">
        <IconPlay size={15} />
      </span>
      <span className="rr__cap">
        {reel.caption ? <b>{reel.caption}</b> : null}
        {who || views ? <span>{[who, views].filter(Boolean).join(' · ')}</span> : null}
      </span>
      {children}
    </button>
  );
}
